import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, ArrowUpRight, ChevronLeft, Compass } from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CompleteYourProject from '../components/affiliate/CompleteYourProject';
import WelcomeHomeMoment from '../components/affiliate/WelcomeHomeMoment';
import MakeItHomeBundles from '../components/affiliate/MakeItHomeBundles';
import { LIFE_STAGE_BY_SLUG, LIFE_STAGE_LANES } from '../data/lifeStageLanes';

/**
 * LifeStageHubPage
 *
 * Dynamic hub for each life-stage lane defined in
 * /app/frontend/src/data/lifeStageLanes.js. Renders at
 * `/where-are-you-at/:stage`.
 *
 * Brand-locked to BuildPilot palette (§15.3): navy / blue / cyan /
 * white / hairline / lavender-white. No pastels.
 *
 * Content is 100% curated from existing platform surfaces. This
 * page does not send emails, request contact details or trigger any
 * marketing sequence - it is a discovery hub only.
 */

const openMatcher = (category) => {
  try {
    window.dispatchEvent(
      new CustomEvent('buildpilot:open-matcher', {
        detail: { category: category || 'professional:builder' },
      }),
    );
  } catch {
    /* graceful no-op */
  }
};

const HomeLogPreview = () => (
  <div
    className="rounded-2xl border border-dashed p-6"
    style={{
      borderColor: 'var(--bp-hairline, #e8ebef)',
      backgroundColor: 'var(--bp-surface-tinted, #f0f4fa)',
    }}
    data-testid="home-log-preview"
  >
    <div
      className="text-[11px] font-semibold uppercase mb-2"
      style={{ color: '#1d8efe', letterSpacing: '0.18em' }}
    >
      Coming next
    </div>
    <h3
      className="text-lg font-semibold mb-1"
      style={{ color: 'var(--bp-color-ink, #1d1d1f)' }}
    >
      Home Log
    </h3>
    <p
      className="text-sm leading-relaxed mb-4"
      style={{ color: 'var(--bp-quiet, #86868b)' }}
    >
      A private log for appliance warranties, receipts, maintenance dates and defect list items. Persists for the life of your home. Currently in build - your first-year Home Log will land in your workspace soon.
    </p>
    <span
      className="inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium"
      style={{
        borderColor: 'var(--bp-hairline, #e8ebef)',
        color: 'var(--bp-quiet, #86868b)',
        backgroundColor: '#fff',
      }}
    >
      In development
    </span>
  </div>
);

const SectionBlock = ({ section }) => {
  const primary = section.primaryCta;
  return (
    <div
      className="rounded-2xl border p-6"
      style={{
        borderColor: 'var(--bp-hairline, #e8ebef)',
        backgroundColor: 'var(--bp-surface, #ffffff)',
      }}
    >
      <h3
        className="text-lg font-semibold mb-4"
        style={{ color: 'var(--bp-color-ink, #1d1d1f)' }}
      >
        {section.heading}
      </h3>

      {primary && (
        <button
          type="button"
          onClick={() => openMatcher(primary.opensMatcher)}
          className="mb-4 inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold text-white transition hover:opacity-90"
          style={{ backgroundColor: '#0a2540' }}
          data-testid={`hub-primary-cta-${section.heading.toLowerCase().replace(/\s+/g, '-')}`}
        >
          {primary.label}
          <ArrowRight className="h-4 w-4" strokeWidth={2.25} />
        </button>
      )}

      {section.preview === 'home-log' && <HomeLogPreview />}

      {section.links && section.links.length > 0 && (
        <ul className="space-y-2">
          {section.links.map((link) => (
            <li key={link.to}>
              <Link
                to={link.to}
                className="group flex items-center justify-between rounded-xl px-3 py-2 text-sm transition"
                style={{ color: 'var(--bp-color-ink, #1d1d1f)' }}
                data-testid={`hub-link-${link.to.replace(/\W+/g, '-').replace(/^-|-$/g, '')}`}
              >
                <span>{link.label}</span>
                <ArrowUpRight
                  className="h-4 w-4 transform transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                  style={{ color: 'var(--bp-quiet, #86868b)' }}
                  strokeWidth={2.25}
                />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// Each life-stage lane maps to one of the 5 build-journey stages. The
// stage-aware product strip only renders where gear is actually relevant
// (build/own); earlier stages return nothing, so products appear once the
// visitor has reached the point in their journey where they'd buy.
const LANE_STAGE = {
  'just-curious': 'dream',
  'saving-up': 'plan',
  'ready-to-build': 'choose',
  'building-now': 'build',
  'just-moved-in': 'own',
  'been-here-a-while': 'own',
};

const LifeStageHubPage = () => {
  const { stage } = useParams();
  const lane = LIFE_STAGE_BY_SLUG[stage];

  if (!lane) {
    return <Navigate to="/where-are-you-at" replace />;
  }
  const canonical = `https://buildpilot.com.au/where-are-you-at/${lane.slug}`;

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--bp-surface, #ffffff)' }}>
      <Helmet>
        <title>{`${lane.title} | Where you are at | BuildPilot`}</title>
        <meta
          name="description"
          content={`${lane.emotion} BuildPilot has content, tools and human help for the ${lane.eyebrow.toLowerCase()} stage of your home journey.`}
        />
        <link rel="canonical" href={canonical} />
      </Helmet>

      <Header variant="light" />

      <main data-testid={`life-stage-hub-${lane.slug}`}>
        <section
          className="border-b bg-white"
          style={{
            borderColor: 'var(--bp-hairline, #e8ebef)',
          }}
        >
          <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20">
            <Link
              to="/where-are-you-at"
              className="inline-flex items-center gap-1 text-sm font-medium mb-6 transition"
              style={{ color: 'var(--bp-quiet, #86868b)' }}
              data-testid="hub-back-link"
            >
              <ChevronLeft className="h-4 w-4" strokeWidth={2.25} />
              All life stages
            </Link>
            <div
              className="text-[11px] font-semibold uppercase mb-2"
              style={{ color: '#0a2540', letterSpacing: '0.22em' }}
            >
              {lane.eyebrow}
            </div>
            <h1
              className="text-[32px] sm:text-5xl font-bold tracking-tight leading-tight mb-4"
              style={{ color: 'var(--bp-color-ink, #1d1d1f)' }}
            >
              {lane.title}
            </h1>
            <p
              className="text-lg max-w-3xl leading-relaxed mb-2"
              style={{ color: 'var(--bp-color-ink, #1d1d1f)' }}
            >
              {lane.emotion}
            </p>
            {lane.ageWhisper && (
              <p
                className="text-sm"
                style={{ color: 'var(--bp-quiet, #86868b)' }}
              >
                {lane.ageWhisper}
              </p>
            )}
          </div>
        </section>

        <section className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-14">
          <div className="max-w-3xl mb-10">
            <h2
              className="text-[22px] sm:text-3xl font-semibold mb-3"
              style={{ color: 'var(--bp-color-ink, #1d1d1f)' }}
            >
              {lane.hub.heading}
            </h2>
            <p
              className="text-base leading-relaxed"
              style={{ color: 'var(--bp-quiet, #86868b)' }}
            >
              {lane.hub.intro}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {lane.hub.sections.map((section, idx) => (
              <SectionBlock key={`${section.heading}-${idx}`} section={section} />
            ))}
          </div>
        </section>

        {LANE_STAGE[lane.slug] === 'own' ? (
          <section className="border-t" style={{ borderColor: 'var(--bp-hairline, #e8ebef)' }}>
            <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
              <MakeItHomeBundles celebrate />
            </div>
          </section>
        ) : LANE_STAGE[lane.slug] && (
          <section className="border-t py-12" style={{ borderColor: 'var(--bp-hairline, #e8ebef)' }}>
            <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
              <CompleteYourProject
                stage={LANE_STAGE[lane.slug]}
                variant="full"
                limit={3}
                perNeed={4}
                heading="Gear people reach for at this stage"
                intro="Independent picks that suit where you're at — no pressure, and you buy direct from the retailer at their price."
              />
            </div>
          </section>
        )}

        <section
          className="border-t py-12"
          style={{
            backgroundColor: 'var(--bp-surface-tinted, #f0f4fa)',
            borderColor: 'var(--bp-hairline, #e8ebef)',
          }}
        >
          <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
            <div
              className="text-[11px] font-semibold uppercase mb-3"
              style={{ color: 'var(--bp-quiet, #86868b)', letterSpacing: '0.18em' }}
            >
              Not quite you?
            </div>
            <h2
              className="text-xl sm:text-2xl font-semibold mb-6"
              style={{ color: 'var(--bp-color-ink, #1d1d1f)' }}
            >
              Jump into a different stage
            </h2>
            <div className="flex flex-wrap gap-2">
              {LIFE_STAGE_LANES.filter((other) => other.slug !== lane.slug).map((other) => (
                <Link
                  key={other.slug}
                  to={`/where-are-you-at/${other.slug}`}
                  className="inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-medium transition hover:opacity-80"
                  style={{
                    borderColor: 'var(--bp-hairline, #e8ebef)',
                    backgroundColor: '#fff',
                    color: 'var(--bp-color-ink, #1d1d1f)',
                  }}
                  data-testid={`hub-other-stage-${other.slug}`}
                >
                  <Compass className="h-3.5 w-3.5" style={{ color: 'var(--bp-quiet, #86868b)' }} strokeWidth={2.25} />
                  {other.eyebrow}
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default LifeStageHubPage;
